import { AssignedWorkflowPhaseDto } from '@act/shared/data-transfer-objects';
import {
  PayloadAction,
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import { addToEffectsMiddleware } from 'redux/utils/effects-middleware';
import { workflowsRequests } from '../requests/workflows.request';
import { useAppSelector } from '../store-hooks';
import { WorkflowsState } from '../types/workflowTypes';
import { createApiThunk } from '../utils/api-thunk';
import { feedSlice, getFeedItems, getPendingItems } from './feedSlice';
import { selectSelectedMemberId } from './selectedMemberSlice';
import { resetRedux } from '../actions/shared-actions';

// THUNKS/ACTIONS ==============================================================
export const getAssignedWorkflowPhaseById = createApiThunk(
  workflowsRequests.getAssignedWorkflowPhaseById
);
export const getAvailableAssignedWorkflowPhasesByUserId = createApiThunk(
  workflowsRequests.getAvailableAssignedWorkflowPhasesByUserId
);
export const createWorkflowScheduledEvent = createApiThunk(
  workflowsRequests.createWorkflowScheduledEvent
);
export const deleteAssignedWorkflowScheduledEvent = createApiThunk(
  workflowsRequests.deleteAssignedWorkflowScheduledEvent
);
export const completeAssignedWorkflowPhase = createApiThunk(
  workflowsRequests.completeAssignedWorkflowPhase
);

export const loadWorkflows = createAction('workflows/load-available');

// INITIAL STATE ===============================================================
export const assignedWorkflowPhaseAdapter =
  createEntityAdapter<AssignedWorkflowPhaseDto>();

const initialState: WorkflowsState = {
  status: 'initial',
  assignedWorkflowPhases: assignedWorkflowPhaseAdapter.getInitialState(),
};

// SLICE =======================================================================
export const workflowsSlice = createSlice({
  name: 'workflows',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(feedSlice.actions.feedItemUpdated, (state, action) => {
        if (!action.payload.feedItemModelView.assignedWorkflowPhases) return;

        assignedWorkflowPhaseAdapter.upsertMany(
          state.assignedWorkflowPhases,
          action.payload.feedItemModelView.assignedWorkflowPhases
        );
      })
      .addCase(getFeedItems.fulfilled, (state, action) => {
        assignedWorkflowPhaseAdapter.addMany(
          state.assignedWorkflowPhases,
          action.payload.assignedWorkflowPhases
        );
        state.status = 'success';
      })
      .addCase(getAvailableAssignedWorkflowPhasesByUserId.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        getAvailableAssignedWorkflowPhasesByUserId.fulfilled,
        (
          state: WorkflowsState,
          action: PayloadAction<AssignedWorkflowPhaseDto[]>
        ) => {
          state.status = 'success';
          state.assignedWorkflowPhases = assignedWorkflowPhaseAdapter.addMany(
            state.assignedWorkflowPhases,
            action.payload
          );
        }
      )
      .addCase(getAvailableAssignedWorkflowPhasesByUserId.rejected, state => {
        state.status = 'failed';
      })

      .addCase(createWorkflowScheduledEvent.pending, state => {
        state.status = 'loading';
      })
      .addCase(createWorkflowScheduledEvent.fulfilled, state => {
        state.status = 'success';
      })
      .addCase(createWorkflowScheduledEvent.rejected, state => {
        state.status = 'failed';
      })

      .addCase(deleteAssignedWorkflowScheduledEvent.pending, state => {
        state.status = 'loading';
      })
      .addCase(deleteAssignedWorkflowScheduledEvent.fulfilled, state => {
        state.status = 'success';
      })
      .addCase(deleteAssignedWorkflowScheduledEvent.rejected, state => {
        state.status = 'failed';
      })

      .addCase(getAssignedWorkflowPhaseById.pending, state => {
        state.status = 'loading';
      })
      .addCase(getAssignedWorkflowPhaseById.fulfilled, (state, action) => {
        state.status = 'success';
        state.assignedWorkflowPhases = assignedWorkflowPhaseAdapter.addOne(
          state.assignedWorkflowPhases,
          action.payload
        );
      })
      .addCase(getAssignedWorkflowPhaseById.rejected, state => {
        state.status = 'failed';
      })

      .addCase(completeAssignedWorkflowPhase.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        completeAssignedWorkflowPhase.fulfilled,
        (state, action: PayloadAction<AssignedWorkflowPhaseDto>) => {
          const phase = action.payload;
          assignedWorkflowPhaseAdapter.upsertOne(
            state.assignedWorkflowPhases,
            phase
          );
          state.status = 'success';
        }
      )
      .addCase(completeAssignedWorkflowPhase.rejected, state => {
        state.status = 'failed';
      })
      // Pending Item Workflows ===========================================
      .addCase(getPendingItems.pending, state => {
        state.status = 'loading';
      })
      .addCase(getPendingItems.fulfilled, (state, action) => {
        assignedWorkflowPhaseAdapter.upsertMany(
          state.assignedWorkflowPhases,
          action.payload.workflowPhases
        );
        state.status = 'success';
      })
      .addCase(getPendingItems.rejected, state => {
        state.status = 'failed';
      });
  },
});

// REDUCER =====================================================================
export const workflowsReducer = workflowsSlice.reducer;

// SELECTORS ===================================================================
interface AvailablePhasesSelectorParams {
  memberId: string;
  [key: string]: unknown;
}

export const selectWorkflowState = state =>
  (state as { workflows: typeof initialState })?.workflows;

export const { selectAll: selectAssignedWorkflowPhases } =
  assignedWorkflowPhaseAdapter.getSelectors(
    state => selectWorkflowState(state).assignedWorkflowPhases
  );

export const selectAssignedWorkflowPhaseById = (id: number) =>
  createSelector(selectWorkflowState, workflowState => {
    return workflowState.assignedWorkflowPhases.entities[id];
  });

export const selectAvailableWorkflowPhases: (
  state,
  paramObj: AvailablePhasesSelectorParams
) => AssignedWorkflowPhaseDto[] = createSelector(
  [
    selectAssignedWorkflowPhases,
    (state, paramObj: AvailablePhasesSelectorParams) => paramObj.memberId,
  ],
  (assignedWorkflowPhases, memberId) =>
    assignedWorkflowPhases.filter(
      awp =>
        awp.userId === memberId &&
        !awp.completedAt &&
        DateTime.fromMillis(awp.availableDate) < DateTime.now()
    )
);

export const useWorkflowSelectors = {
  useSelectAvailableWorkflowPhases: (memberId: string) =>
    useAppSelector(state => selectAvailableWorkflowPhases(state, { memberId })),
};

// EXPORT HELPER ===============================================================

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: loadWorkflows,
    effect: async ({ dispatch, state }) => {
      const selectedMemberId = selectSelectedMemberId(state);
      dispatch(
        getAvailableAssignedWorkflowPhasesByUserId({
          params: { userId: selectedMemberId },
        })
      );
    },
  });
});

// WEBSOCKET ACTIONS ===========================================================
